import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import { getSession, signOut } from 'next-auth/react'
import { LogError } from './sentryFn'
import toast from 'react-hot-toast'

interface IRequestOptions {
  url: string
  config?: AxiosRequestConfig
  withToken?: boolean
}

/**
 * The `validateURL` function is a TypeScript decorator that validates if a URL starts and ends with a
 * forward slash.
 * @param {any} target - The `target` parameter refers to the object that owns the method being
 * decorated. In other words, it is the class prototype or constructor function.
 * @param {string} propertyKey - The `propertyKey` parameter represents the name of the property
 * (method) that the decorator is being applied to.
 * @param {PropertyDescriptor} descriptor - The `descriptor` parameter is an object that represents the
 * property descriptor of the method being decorated. It contains information about the method such as
 * its value, configurable, enumerable, and writable attributes.
 * @returns The `descriptor` object is being returned.
 */
function validateURL(
  target: any,
  propertyKey: string,
  descriptor: PropertyDescriptor
) {
  const originalMethod = descriptor.value

  descriptor.value = function (...args: any[]) {
    const [options, ...rest] = args
    const { url } = options
    if (!url.startsWith('/')) {
      throw new Error('Invalid URL')
    }

    if (!url.includes('?') && !url.endsWith('/')) {
      throw new Error('Invalid URL')
    }

    return originalMethod.apply(this, [options, ...rest])
  }

  return descriptor
}

class ApiService {
  private axiosInstance

  constructor(private baseURL: string) {
    this.axiosInstance = axios.create({
      baseURL,
    })

    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: any) => {
        if (error.response.status === 401) {
          signOut({
            callbackUrl: '/auth',
          })
          return
        }
        toast.error(error.response.data.details || error.response.data.message)

        LogError({
          error,
          title: 'axiosInstance',
        })

        throw error
      }
    )
  }

  /**
   * The function `getToken` retrieves the token from the user's session and returns it as a string.
   * @returns the token value from the session object.
   */
  private async getToken(): Promise<string> {
    if (process.env.NODE_ENV === 'test') {
      return 'TEST'
    }

    const session = await getSession()
    // @ts-ignore
    if (!session?.user?.token) {
      signOut({
        callbackUrl: '/auth',
      })
      throw new Error('No token found')
    }

    // @ts-ignore
    return session.user.token
  }

  /**
   * The `request` function is a private asynchronous method that sends an HTTP request using Axios, with
   * an optional token for authorization, and returns the response data.
   * @param {AxiosRequestConfig} config - The `config` parameter is an object that contains the
   * configuration options for the Axios request. It can include properties such as `url`, `method`,
   * `params`, `data`, and `headers`.
   * @param {boolean} [withToken=true] - The `withToken` parameter is a boolean flag that determines
   * whether or not to include an authorization token in the request headers. If `withToken` is `true`,
   * the function will retrieve a token using the `getToken()` method and include it in the
   * `Authorization` header as a bearer token.
   * @returns the `data` property of the `response` object.
   */
  private async request(
    config: AxiosRequestConfig,
    withToken: boolean = true
  ): Promise<any> {
    try {
      const headers = config.headers || {}
      if (withToken) {
        const token = await this.getToken()
        headers.Authorization = `Bearer ${token}`
      }
      return await this.axiosInstance({
        ...config,
        baseURL: this.baseURL,
        headers: {
          ...headers,
        },
      })
    } catch (error: any) {
      LogError({
        error,
        title: 'request',
      })
      throw error
    }
  }

  // @validateURL
  /**
   * The `get` function is an asynchronous function that makes a GET request to a specified URL with
   * optional configuration and token.
   * @param {IRequestOptions}  - - `url`: The URL of the API endpoint to make the GET request to.
   * @returns The `get` method is returning a Promise that resolves to an unspecified value.
   */
  async get({ url, config, withToken }: IRequestOptions): Promise<any> {
    return this.request({ method: 'GET', url, ...config }, withToken)
  }

  // @validateURL
  /**
   * The function is an asynchronous method that sends a POST request to a specified URL with optional
   * configuration and authentication token.
   * @param {IRequestOptions}  - - `url`: The URL to which the POST request will be sent.
   * @returns The `post` function is returning a Promise that resolves to `any`.
   */
  async post({ url, config, withToken }: IRequestOptions): Promise<any> {
    return this.request({ method: 'POST', url, ...config }, withToken)
  }

  // @validateURL
  /**
   * The `put` function is an asynchronous function that sends a PUT request to a specified URL with
   * optional configuration and authentication token.
   * @param {IRequestOptions}  - - `url`: The URL to which the PUT request will be sent.
   * @returns The `put` method is returning a Promise that resolves to `any`.
   */
  async put({ url, config, withToken }: IRequestOptions): Promise<any> {
    return this.request({ method: 'PUT', url, ...config }, withToken)
  }

  // @validateURL
  /**
   * The function is an async method that sends a PATCH request to a specified URL with optional
   * configuration and authentication token.
   * @param {IRequestOptions}  - - `url`: The URL to send the PATCH request to.
   * @returns The `patch` method is returning a Promise that resolves to `any`.
   */
  async patch({ url, config, withToken }: IRequestOptions): Promise<any> {
    return this.request({ method: 'PATCH', url, ...config }, withToken)
  }

  // @validateURL
  /**
   * The `delete` function sends a DELETE request to the specified URL with the provided configuration
   * and optional token.
   * @param {IRequestOptions}  - - `url`: The URL of the resource to be deleted.
   * @returns The `delete` method is returning a Promise that resolves to `any`.
   */
  async delete({ url, config, withToken }: IRequestOptions): Promise<any> {
    return this.request({ method: 'DELETE', url, ...config }, withToken)
  }
}

// Create an instance of ApiService with the base URL
const apiService = new ApiService(process.env.NEXT_PUBLIC_BASE_API_URL || '')

export default apiService
